import React from 'react';
import { useHistory } from 'react-router-dom';

import {
  AnyAssetApplication,
  assetTypeLabel,
  openNotificationWithIcon,
  useAssetParams,
  useAssetQuery,
} from '@mss/mss-component-library';
import { Modal, notification } from 'antd';
import { useTranslation } from 'react-i18next';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBan,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { deleteAsset } from '../services/delete';
import Button from '../../Button';
import { useQueryClient } from '@tanstack/react-query';

export default function DeleteAssetButton() {
  const history = useHistory();
  const assetParams = useAssetParams();
  const { category, assetType } = assetParams;
  const { data: asset } = useAssetQuery<AnyAssetApplication>();
  const [t] = useTranslation(['asset', 'common']);
  const queryClient = useQueryClient();

  // remove last char
  const categorySingle = category.slice(0, -1);

  const deleteAssetModal = async (asset: AnyAssetApplication) =>
    Modal.confirm({
      title: t('Are you sure you want to delete?'),
      icon: (
        <span className="anticon">
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            color="#fd9644"
            size="sm"
          />
        </span>
      ),
      content: (
        <div>
          <blockquote>{asset.name}</blockquote>
          <p>{t(`You're about to delete this ${categorySingle}.`)}</p>
        </div>
      ),
      onOk: async () => {
        const result = await deleteAsset(assetParams);
        if (result.status === 204) {
          openNotificationWithIcon(
            notification.success,
            t(`((assetLabel)) ${categorySingle} deleted`, {
              assetLabel: assetTypeLabel(asset as any),
              interpolation: { escapeValue: false },
            }),
            t('((assetLabel)) deleted successfully', {
              assetLabel: asset.name,
              interpolation: { escapeValue: false },
            })
          );
          queryClient.setQueryData(['list', assetType], oldData =>
            Array.isArray(oldData)
              ? oldData.filter(x => x.id !== assetParams.assetId)
              : []
          );
          await queryClient.invalidateQueries({
            queryKey: ['list', assetType],
          });
          history.push(`/${category}/${assetType}`);
        } else {
          openNotificationWithIcon(
            notification.error,
            t(`((assetLabel)) ${categorySingle} deletion failed`, {
              assetLabel: assetTypeLabel(asset as any),
              interpolation: { escapeValue: false },
            }),
            t('Reason (REASON)', { reason: result.statusText })
          );
          console.error(t('Error!'), result);
        }
      },
      okButtonProps: { danger: true },
      okText: t('OK'),
      cancelText: t('Cancel'),
    });

  return (
    <Button
      onClick={asset ? () => deleteAssetModal(asset) : undefined}
      data-ta="deleteAssetButton"
    >
      <FontAwesomeIcon icon={faBan} className="mr-1" />
      {!asset
        ? `${t('Loading')}...`
        : t(`DELETE ${category.slice(0, -1).toUpperCase()}`)}
    </Button>
  );
}
